import React, { type PropsWithChildren, type KeyboardEvent, type ReactNode } from 'react';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { messages } from './messages.tsx';

const styles = cssMap({
	floatingContainerOld: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		backgroundColor: token('color.background.neutral.bold.pressed'),
		borderRadius: token('radius.xlarge'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	floatingContainer: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	contentContainerOld: {
		height: '32px',
		color: token('color.text.inverse'),
		fontVariantNumeric: 'tabular-nums',
	},
	contentContainer: {
		height: '32px',
		fontVariantNumeric: 'tabular-nums',
	},
	separatorOld: {
		height: '16px',
		width: '1px',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.xsmall'),
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
	},
	separator: {
		backgroundColor: token('color.background.accent.gray.bolder'),
		height: '16px',
		width: '1px',
		borderRadius: token('radius.xsmall'),
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
	},
	sideContentContainer: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	mainContainer: {
		pointerEvents: 'auto',
	},
});

type FloatingToolbarContainerProps = {
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
	children?: ReactNode;
};

export const FloatingToolbarContainer = ({
	children,
	onKeyDown,
}: PropsWithChildren<FloatingToolbarContainerProps>): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			role="toolbar"
			xcss={
				expVal('sunsetting_bulk_edit', 'isEnabled', false)
					? styles.floatingContainer
					: styles.floatingContainerOld
			}
			aria-label={formatMessage(messages.toolbarName)}
			onKeyDown={onKeyDown}
		>
			{children}
		</Box>
	);
};

export const FloatingToolbarContent = ({ children }: PropsWithChildren): React.JSX.Element => (
	<Inline
		xcss={
			expVal('sunsetting_bulk_edit', 'isEnabled', false)
				? styles.contentContainer
				: styles.contentContainerOld
		}
		alignBlock="center"
	>
		{children}
	</Inline>
);

export const FloatingToolbarSeparator = (): React.JSX.Element => (
	<Box
		xcss={
			expVal('sunsetting_bulk_edit', 'isEnabled', false) ? styles.separator : styles.separatorOld
		}
	/>
);

export const FloatingToolbarSideContent = ({ children }: PropsWithChildren): React.JSX.Element => (
	<Box xcss={styles.sideContentContainer}>{children}</Box>
);

export const ContentSlider = ({
	children,
	show,
}: {
	children: React.ReactNode;
	show: boolean;
}): React.JSX.Element => (
	<ExitingPersistence>
		{show && (
			<SlideIn enterFrom="bottom" fade="inout">
				{(slideInProps) => (
					<div {...slideInProps}>
						<Box xcss={styles.mainContainer}>{children}</Box>
					</div>
				)}
			</SlideIn>
		)}
	</ExitingPersistence>
);
